import React from 'react';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { Button } from '@/components/ui/button';
import { Bell, BellRing, Smartphone, Share, Plus, Check, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Banner to guide iOS users through PWA installation and push notification setup
 */
export const PushNotificationBanner = ({ userId, onDismiss }) => {
    const {
        permission,
        isSubscribed,
        isPWA,
        isIOS,
        isLoading,
        isSupported,
        needsPWAInstall,
        subscribe,
        testPush
    } = usePushNotifications(userId);

    // Already subscribed - show success state briefly or nothing
    if (isSubscribed) {
        return null;
    }

    // Not supported at all
    if (!isSupported && !isIOS) {
        return null;
    }

    // iOS Safari - needs PWA install first
    if (needsPWAInstall) {
        return (
            <div className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 border border-amber-500/30 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                    <Smartphone className="w-8 h-8 text-amber-400 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                        <h3 className="text-amber-300 font-bold text-lg mb-1">
                            📱 Installa Don Doit sul tuo iPhone!
                        </h3>
                        <p className="text-amber-200/80 text-sm mb-3">
                            Per ricevere notifiche push, aggiungi l'app alla Home Screen:
                        </p>
                        <ol className="text-amber-200/80 text-sm space-y-2 mb-3">
                            <li className="flex items-center gap-2">
                                <span className="bg-amber-500/20 w-6 h-6 rounded-full flex items-center justify-center text-amber-300 text-xs font-bold">1</span>
                                Tocca il pulsante <Share className="w-4 h-4 inline mx-1" /> Condividi
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="bg-amber-500/20 w-6 h-6 rounded-full flex items-center justify-center text-amber-300 text-xs font-bold">2</span>
                                Scorri e tocca <Plus className="w-4 h-4 inline mx-1" /> "Aggiungi a Home"
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="bg-amber-500/20 w-6 h-6 rounded-full flex items-center justify-center text-amber-300 text-xs font-bold">3</span>
                                Apri l'app dalla Home Screen
                            </li>
                        </ol>
                        <p className="text-amber-400/60 text-xs italic">
                            ⚠️ Le notifiche push su iPhone funzionano solo dall'app installata
                        </p>
                    </div>
                    {onDismiss && (
                        <button onClick={onDismiss} className="text-amber-400/50 hover:text-amber-300">
                            <X className="w-5 h-5" />
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // In PWA or on desktop/Android - can enable notifications
    if (permission === 'default' || permission === 'granted') {
        return (
            <div className="bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border border-emerald-500/30 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-3">
                    <Bell className="w-8 h-8 text-emerald-400 flex-shrink-0" />
                    <div className="flex-1">
                        <h3 className="text-emerald-300 font-bold text-lg">
                            🔔 Attiva le notifiche push!
                        </h3>
                        <p className="text-emerald-200/80 text-sm">
                            Ricevi ordini dal Don anche quando l'app è chiusa.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={subscribe}
                            disabled={isLoading}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white"
                        >
                            {isLoading ? (
                                <span className="animate-pulse">Attivando...</span>
                            ) : (
                                <>
                                    <BellRing className="w-4 h-4 mr-2" />
                                    Attiva
                                </>
                            )}
                        </Button>
                        {onDismiss && (
                            <button onClick={onDismiss} className="text-emerald-400/50 hover:text-emerald-300 px-2">
                                <X className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Permission denied
    if (permission === 'denied') {
        return (
            <div className="bg-gradient-to-r from-red-900/30 to-rose-900/30 border border-red-500/30 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-3">
                    <X className="w-8 h-8 text-red-400 flex-shrink-0" />
                    <div className="flex-1">
                        <h3 className="text-red-300 font-bold">
                            ⚠️ Notifiche bloccate!
                        </h3>
                        <p className="text-red-200/80 text-sm">
                            Hai bloccato le notifiche. Il Don non può contattarti!
                        </p>
                        <p className="text-red-400 text-xs mt-2 font-bold uppercase tracking-tight">
                            Per sbloccare: Clicca l'icona lucchetto (o impostazioni) nella barra del browser e attiva "Notifiche".
                        </p>
                    </div>
                    {onDismiss && (
                        <button onClick={onDismiss} className="text-red-400/50 hover:text-red-300">
                            <X className="w-5 h-5" />
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return null;
};

/**
 * Small notification toggle button for settings/header
 */
export const PushNotificationToggle = ({ userId }) => {
    const { isSubscribed, subscribe, unsubscribe, isLoading, testPush, permission } = usePushNotifications(userId);

    if (isSubscribed) {
        return (
            <div className="flex items-center gap-2">
                <span className="text-emerald-400 flex items-center gap-1 text-sm">
                    <Check className="w-4 h-4" />
                    Push attive
                </span>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={testPush}
                    className="text-xs text-gray-400 hover:text-white"
                >
                    Test
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={unsubscribe}
                    disabled={isLoading}
                    className="text-xs text-red-400 hover:text-red-300"
                >
                    Disattiva
                </Button>
            </div>
        );
    }

    // Permission denied state
    if (permission === 'denied') {
        return (
            <Button
                variant="ghost"
                size="sm"
                className="text-red-400 opacity-70 hover:opacity-100"
                onClick={() => toast.error("Notifiche Bloccate", {
                    description: "Sblocca le notifiche nelle impostazioni del browser (icona lucchetto nella barra indirizzi)."
                })}
            >
                <X className="w-4 h-4 mr-2" />
                Push Bloccate
            </Button>
        );
    }

    return (
        <Button
            variant="outline"
            size="sm"
            onClick={subscribe}
            disabled={isLoading}
            className="text-amber-400 border-amber-400/30 hover:bg-amber-400/10"
        >
            <Bell className="w-4 h-4 mr-2 animate-pulse" />
            {isLoading ? 'Attivando...' : 'Attiva Push'}
        </Button>
    );
};

export default PushNotificationBanner;
